import { Button, Modal, Progress } from "@devhop/ui";
import { LoaderIcon, UploadIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { inventoryApi } from "../api/client";

type StockCsvImportDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

type ParseResult =
  | {
      ok: true;
      variantId: string;
      quantity: number;
      lowStockThreshold: number | null;
    }
  | { ok: false; error: string };

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let i = 0;
  while (i < line.length) {
    if (line[i] === '"') {
      let s = "";
      i++;
      while (i < line.length) {
        if (line[i] === '"') {
          i++;
          if (line[i] === '"') {
            s += '"';
            i++;
          } else break;
        } else {
          s += line[i];
          i++;
        }
      }
      out.push(s);
      if (line[i] === ",") i++;
    } else {
      let s = "";
      while (i < line.length && line[i] !== ",") {
        s += line[i];
        i++;
      }
      out.push(s.trim());
      if (line[i] === ",") i++;
    }
  }
  return out;
}

function parseCsv(text: string): string[][] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  return lines.map(parseCsvLine);
}

function parseRow(
  headers: string[],
  values: string[],
  rowIndex: number,
): ParseResult {
  const get = (name: string): string => {
    const i = headers.findIndex(
      (h) => h.trim().toLowerCase() === name.toLowerCase(),
    );
    if (i < 0 || i >= values.length) return "";
    return values[i]?.trim() ?? "";
  };
  const variantId = get("variantId");
  if (!variantId) {
    return { ok: false, error: `ແຖວ ${rowIndex + 1}: ບໍ່ພົບ variantId` };
  }
  const qStr = get("quantity");
  const q = qStr === "" ? Number.NaN : Number.parseInt(qStr, 10);
  if (Number.isNaN(q) || q < 0) {
    return {
      ok: false,
      error: `ແຖວ ${rowIndex + 1}: quantity ຕ້ອງເປັນຕົວເລກບວກ (ມີ: ${qStr || "ວ່າງ"})`,
    };
  }
  const tStr = get("lowStockThreshold");
  let lowStockThreshold: number | null = null;
  if (tStr !== "") {
    const t = Number.parseInt(tStr, 10);
    if (Number.isNaN(t) || t < 0) {
      return {
        ok: false,
        error: `ແຖວ ${rowIndex + 1}: lowStockThreshold ຕ້ອງເປັນຕົວເລກບວກຫຼືວ່າງ (ມີ: ${tStr})`,
      };
    }
    lowStockThreshold = t;
  }
  return { ok: true, variantId, quantity: q, lowStockThreshold };
}

export function StockCsvImportDialog({
  open,
  onOpenChange,
  onSuccess,
}: StockCsvImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<{
    success: number;
    failed: number;
    errors: string[];
  } | null>(null);

  const reset = useCallback(() => {
    setFile(null);
    setProgress(0);
    setResult(null);
  }, []);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) reset();
      onOpenChange(next);
    },
    [onOpenChange, reset],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      setFile(f ?? null);
      setResult(null);
    },
    [],
  );

  const handleImport = useCallback(async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    setProgress(0);
    const errors: string[] = [];
    let success = 0;
    let failed = 0;
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length < 2) {
        setResult({
          success: 0,
          failed: 0,
          errors: ["ບໍ່ພົບແຖວຂໍ້ມູນ (ຕ້ອງມີ header ແລະຢ່າງໜ້ອຍ 1 ແຖວ)"],
        });
        setLoading(false);
        return;
      }
      const headers = rows[0];
      const dataRows = rows.slice(1);
      const parsed: ParseResult[] = [];
      for (let i = 0; i < dataRows.length; i++) {
        const r = parseRow(headers, dataRows[i], i);
        parsed.push(r);
        if (!r.ok) {
          errors.push(r.error);
          failed++;
        }
      }
      const toProcess = parsed.filter(
        (p): p is ParseResult & { ok: true } => p.ok,
      );
      const total = toProcess.length;
      for (let i = 0; i < toProcess.length; i++) {
        const row = toProcess[i];
        try {
          await inventoryApi.stock.adjust({
            variantId: row.variantId,
            type: "restock",
            quantity: row.quantity,
          });
          if (row.lowStockThreshold !== undefined) {
            await inventoryApi.stock.updateThreshold(row.variantId, {
              variantId: row.variantId,
              lowStockThreshold: row.lowStockThreshold,
            });
          }
          success++;
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : String(err);
          errors.push(`ແຖວ variantId=${row.variantId}: ${msg}`);
          failed++;
        }
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      setResult({ success, failed, errors });
      if (failed === 0 && success > 0 && onSuccess) onSuccess();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setResult({
        success: 0,
        failed: 0,
        errors: [`ອ່ານໄຟລ໌ບໍ່ສຳເລັດ: ${msg}`],
      });
    } finally {
      setLoading(false);
      setProgress(0);
    }
  }, [file, onSuccess]);

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      title="ນຳເຂົ້າສະຕ໋ອກຈາກ CSV"
      description="ເລືອກໄຟລ໌ CSV ທີ່ສົ່ງອອກຈາກ template ແລ້ວແກ້ໄຂ (variantId, quantity, lowStockThreshold)"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={loading}
            className="text-sm file:mr-2 file:rounded file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-primary-foreground file:text-sm"
          />
          {file && (
            <span className="text-muted-foreground text-sm">{file.name}</span>
          )}
        </div>
        {loading && (
          <div className="flex flex-col gap-2">
            <Progress value={progress} className="h-2" />
            <p className="text-muted-foreground text-sm">
              ກຳລັງອັບເດດ… {progress}%
            </p>
          </div>
        )}
        {result && !loading && (
          <div className="rounded-lg border bg-muted/30 p-3 text-sm">
            <p>
              ສຳເລັດ {result.success} ລາຍການ
              {result.failed > 0 && `, ລົ້ມເຫຼວ ${result.failed} ລາຍການ`}
            </p>
            {result.errors.length > 0 && (
              <ul className="mt-2 list-inside list-disc text-destructive text-xs">
                {result.errors.slice(0, 10).map((e, idx) => (
                  <li key={`${idx}-${e}`}>{e}</li>
                ))}
                {result.errors.length > 10 && (
                  <li>… ແລະອີກ {result.errors.length - 10} ຂໍ້ຜິດພາດ</li>
                )}
              </ul>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={loading}
          >
            ປິດ
          </Button>
          <Button onClick={handleImport} disabled={!file || loading}>
            {loading ? (
              <LoaderIcon className="h-4 w-4 animate-spin" />
            ) : (
              <UploadIcon className="h-4 w-4" />
            )}
            ນຳເຂົ້າ
          </Button>
        </div>
      </div>
    </Modal>
  );
}
